import apiService from './apiService';

// Helper to format dates to YYYY-MM-DD (backend expected format)
const formatDateParam = (date: string | undefined): string | undefined => {
  if (!date) return undefined;
  // If already in YYYY-MM-DD format, return as-is
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
  // Convert ISO timestamp to YYYY-MM-DD
  try {
    return new Date(date).toISOString().split('T')[0];
  } catch {
    return date;
  }
};

const formatCheckInFilters = (filters?: any) => {
  if (!filters) return filters;
  return {
    ...filters,
    startDate: formatDateParam(filters.startDate),
    endDate: formatDateParam(filters.endDate),
  };
};

export const wellnessService = {
  async createCheckIn(checkInData: any): Promise<any> {
    return apiService.post('/wellness/check-ins', checkInData);
  },

  async updateCheckIn(checkInId: string, updates: any): Promise<any> {
    return apiService.put(`/wellness/check-ins/${checkInId}`, updates);
  },

  async getCheckIns(filters?: any): Promise<any[]> {
    const response = await apiService.get('/wellness/check-ins', { params: formatCheckInFilters(filters) });
    // Backend returns { checkIns: [...], total: number }, extract the array
    return Array.isArray(response) ? response : (response?.checkIns || []);
  },

  async getCheckInsWithPagination(filters?: any): Promise<{ checkIns: any[]; total: number }> {
    const response = await apiService.get('/wellness/check-ins', { params: formatCheckInFilters(filters) });
    // Return full response for components that need pagination info
    if (Array.isArray(response)) {
      return { checkIns: response, total: response.length };
    }
    return { checkIns: response?.checkIns || [], total: response?.total || 0 };
  },

  async getTodaysCheckIn(): Promise<any> {
    // Send local date to handle timezone correctly
    const now = new Date();
    const localDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    return apiService.get(`/wellness/check-ins/today?date=${localDate}`);
  },

  async getCheckInById(id: string): Promise<any> {
    return apiService.get(`/wellness/check-ins/${id}`);
  },

  async deleteCheckIn(id: string): Promise<void> {
    return apiService.delete(`/wellness/check-ins/${id}`);
  },

  async getStats(): Promise<any> {
    return apiService.get('/wellness/stats');
  },

  async getPersonalizedQuestions(currentMood?: number, currentEnergy?: number): Promise<any> {
    return apiService.get('/wellness/questions', {
      params: { currentMood, currentEnergy },
    });
  },

  // MIGRATED TO BACKEND: Generate a single deeply personalized reflection prompt
  // Backend endpoint: GET /wellness/reflection-prompt
  // MIGRATED FROM web_old/src/services/openaiService.ts::generateReflectionPrompt()
  async getReflectionPrompt(recentMood?: number, recentThemes?: string[]): Promise<string> {
    const response = await apiService.get('/wellness/reflection-prompt', {
      params: {
        recentMood,
        recentThemes: recentThemes?.join(','),
      },
    });
    // Backend returns { success: true, data: { prompt: string } }
    return response?.prompt || response?.data?.prompt || '';
  },

  async getGrowthInsights(): Promise<any> {
    return apiService.get('/wellness/insights');
  },

  async dismissGrowthInsight(id: string): Promise<void> {
    return apiService.put(`/wellness/insights/${id}/dismiss`);
  },

  async getWeather(latitude: number, longitude: number): Promise<any> {
    return apiService.get('/wellness/weather', {
      params: { latitude, longitude },
    });
  },

  // ============================================
  // Voice Check-ins (separate from regular check-ins)
  // Multiple voice check-ins per day allowed
  // ============================================

  async submitVoiceCheckIn(audioBlob: Blob, mimeType: string = 'audio/webm'): Promise<any> {
    // Convert Blob to File (required by backend)
    const extension = mimeType.includes('mp4') ? 'mp4' : 'webm';
    const audioFile = new File([audioBlob], `voice-checkin-${Date.now()}.${extension}`, {
      type: mimeType,
    });

    const formData = new FormData();
    // Use 'audio' field name to match backend multer config
    formData.append('audio', audioFile);

    // apiService will automatically handle FormData and set correct Content-Type
    return apiService.post('/wellness/voice-check-ins', formData);
  },

  async submitVoiceCheckInSync(audioBlob: Blob, mimeType: string = 'audio/webm'): Promise<any> {
    // Convert Blob to File (required by backend)
    const extension = mimeType.includes('mp4') ? 'mp4' : 'webm';
    const audioFile = new File([audioBlob], `voice-checkin-${Date.now()}.${extension}`, {
      type: mimeType,
    });

    const formData = new FormData();
    // Use 'audio' field name to match backend multer config
    formData.append('audio', audioFile);

    // apiService will automatically handle FormData and set correct Content-Type
    return apiService.post('/wellness/voice-check-ins/sync', formData);
  },

  async getVoiceCheckIns(filters?: any): Promise<any[]> {
    const response = await apiService.get('/wellness/voice-check-ins', { params: formatCheckInFilters(filters) });
    // Backend returns { voiceCheckIns: [...], total: number }
    return Array.isArray(response) ? response : (response?.voiceCheckIns || []);
  },

  async getVoiceCheckInsWithPagination(filters?: any): Promise<{ voiceCheckIns: any[]; total: number }> {
    const response = await apiService.get('/wellness/voice-check-ins', { params: formatCheckInFilters(filters) });
    if (Array.isArray(response)) {
      return { voiceCheckIns: response, total: response.length };
    }
    return { voiceCheckIns: response?.voiceCheckIns || [], total: response?.total || 0 };
  },

  async getVoiceCheckInById(id: string): Promise<any> {
    return apiService.get(`/wellness/voice-check-ins/${id}`);
  },

  async deleteVoiceCheckIn(id: string): Promise<void> {
    return apiService.delete(`/wellness/voice-check-ins/${id}`);
  },

  async getTodayVoiceCheckInsCount(): Promise<number> {
    const response = await apiService.get('/wellness/voice-check-ins/today/count');
    return response?.count || 0;
  },

  async updateVoiceCheckIn(voiceCheckInId: string, updates: {
    mood?: number;
    energy?: number;
    focus?: number;
    motivation?: number;
  }): Promise<any> {
    return apiService.put(`/wellness/voice-check-ins/${voiceCheckInId}`, updates);
  },
};
